import { getFrontendAppInsights } from './appInsights';

/**
 * Parse a TechDocs URL path into structured dimensions.
 *
 * Expected pattern: /docs/:namespace/:kind/:name/*
 * Example: /docs/default/component/jira-mcp/getting-started
 */
export function parseTechDocsPath(pathname: string):
  | {
      namespace: string;
      kind: string;
      name: string;
      docPath: string;
    }
  | undefined {
  const match = pathname.match(
    /^\/docs\/([^/]+)\/([^/]+)\/([^/]+)(?:\/(.*))?$/,
  );
  if (!match) {
    return undefined;
  }
  return {
    namespace: match[1],
    kind: match[2],
    name: match[3],
    docPath: match[4] ?? '',
  };
}

/**
 * Track a TechDocs page view as an Application Insights custom event.
 *
 * Call this whenever the user navigates to a TechDocs page.
 * Emits a `TechDocsPageView` event with structured properties so you
 * can query which docs are being read and how often in Azure.
 *
 * Example KQL:
 * ```
 * customEvents
 * | where name == "TechDocsPageView"
 * | summarize views = count() by tostring(customDimensions.componentName)
 * | order by views desc
 * ```
 */
export function trackTechDocsPageView(pathname: string): void {
  const ai = getFrontendAppInsights();
  if (!ai) {
    return;
  }

  const parsed = parseTechDocsPath(pathname);
  if (!parsed) {
    return;
  }

  ai.trackEvent({
    name: 'TechDocsPageView',
    properties: {
      namespace: parsed.namespace,
      kind: parsed.kind,
      componentName: parsed.name,
      docPath: parsed.docPath,
      fullPath: pathname,
    },
  });
}
